/**
 * LeftShield Portal - Governance Posture Page
 * Displays enforcement frameworks, controls, and overall enforcement status
 *
 * Frameworks list migrated onto the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md` and
 * `.github/context/list-grid-standard-behaviours.context.md`, following the
 * committed reference migration `src/pages/ApiKeysPage.tsx`.
 */
import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Title1,
  Title2,
  Title3,
  Body1,
  Body2,
  Text,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
  ProgressBar,
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  Dropdown,
  Option,
  Divider,
  makeStyles,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  DocumentRegular,
  SettingsRegular,
  ChevronRight24Regular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { complianceApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { NarratorCard } from '../components/common/NarratorCard';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import { PageHeader } from '../components/common/PageHeader';
import type { ComplianceFramework } from '../types';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  ADAPTIVE_GRID_THRESHOLD,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

const getComplianceColor = (score: number): string => {
  if (score >= 90) return tokens.colorPaletteGreenForeground1;
  if (score >= 70) return tokens.colorPaletteYellowForeground1;
  if (score >= 50) return tokens.colorPaletteRedForeground2;
  return tokens.colorPaletteRedForeground1;
};

interface FrameworkConfig {
  notifyOnFailure: boolean;
  notifyOnImprovement: boolean;
  thresholdScore: number;
  autoRemediate: boolean;
  scanSchedule: string;
}

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalXXXL,
  },
  fixed: {
    flexShrink: 0,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
});

/** Query-key base for the frameworks list — the adaptive grid's probe query lives at
 *  `[...FRAMEWORKS_QUERY_KEY_BASE, 'adaptive-probe', ADAPTIVE_GRID_THRESHOLD]`. The
 *  enable/disable mutation's optimistic update targets that compound key directly (the
 *  base key alone is only used for cancel/invalidate, which match by prefix). */
const FRAMEWORKS_QUERY_KEY_BASE = ['complianceFrameworks'] as const;
const FRAMEWORKS_PROBE_KEY = [
  ...FRAMEWORKS_QUERY_KEY_BASE,
  'adaptive-probe',
  ADAPTIVE_GRID_THRESHOLD,
] as const;

/** Wraps the (unpaged) `complianceApi.getFrameworks` response as a `PagedResult` —
 *  `useAdaptiveGridData` then applies search/sort/paging in memory (client mode), since
 *  the enforcement framework catalog is never large enough to warrant a real
 *  server-paged endpoint. */
async function fetchFrameworksPage(params: ListQueryParams): Promise<PagedResult<ComplianceFramework>> {
  const items = await complianceApi.getFrameworks();
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

const frameworksCrumbs: Crumb[] = [{ label: 'Enforcement Frameworks' }];

export const CompliancePage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [frameworkConfig, setFrameworkConfig] = useState<FrameworkConfig>({
    notifyOnFailure: true,
    notifyOnImprovement: false,
    thresholdScore: 80,
    autoRemediate: false,
    scanSchedule: 'daily',
  });

  const { data: dashboard, isLoading: dashboardLoading, isError, error } = useQuery({
    queryKey: ['complianceDashboard'],
    queryFn: () => complianceApi.getDashboard(),
  });

  const { state, setPage, setSort, setSearch } = useListUrlState({ defaultSort: 'name:asc' });

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const grid = useAdaptiveGridData<ComplianceFramework>({
    queryKeyBase: FRAMEWORKS_QUERY_KEY_BASE,
    fetchPage: fetchFrameworksPage,
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['name', 'code'],
    sortState,
  });

  const {
    items: frameworks,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading: frameworksLoading,
    isFetching,
    isError: frameworksError,
    refetch,
  } = grid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after search.
  useEffect(() => {
    if (!frameworksLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [frameworksLoading, state.page, totalPages, setPage]);

  const toggleFrameworkMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string | number; enabled: boolean }) =>
      complianceApi.setFrameworkEnabled(id, enabled),
    onMutate: async ({ id, enabled }) => {
      await queryClient.cancelQueries({ queryKey: FRAMEWORKS_QUERY_KEY_BASE });
      const previous = queryClient.getQueryData<PagedResult<ComplianceFramework>>(FRAMEWORKS_PROBE_KEY);
      queryClient.setQueryData<PagedResult<ComplianceFramework>>(FRAMEWORKS_PROBE_KEY, (old) =>
        old
          ? { ...old, items: old.items.map((f) => (f.id === id ? { ...f, isEnabled: enabled } : f)) }
          : old,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(FRAMEWORKS_PROBE_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FRAMEWORKS_QUERY_KEY_BASE });
      queryClient.invalidateQueries({ queryKey: ['complianceDashboard'] });
    },
  });

  if (dashboardLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading enforcement data..." />
      </div>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing repo-wide unauth-check idiom (see AdminPage, FrameworksPage, etc.); out of scope for this list-only migration.
  const queryError = error as any;
  const isUnauth = queryError?.status === 401 || queryError?.message?.includes('MSAL');
  if (isError && isUnauth) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Governance Posture"
          subtitle="Board-level view of compliance status across all active frameworks"
        />
        <UnauthenticatedBanner feature="governance posture data" />
      </div>
    );
  }

  const actionButtonCount = 1; // chevron-only Actions cell (§18) — read-only list, no Edit/Delete.
  const columnCount = 7;

  const configureDialog = (
    <Dialog open={isConfigDialogOpen} onOpenChange={(_, data) => setIsConfigDialogOpen(data.open)}>
      <DialogTrigger disableButtonEnhancement>
        <Button appearance="primary" icon={<SettingsRegular />}>
          Configure
        </Button>
      </DialogTrigger>
      <DialogSurface style={{ maxWidth: '500px' }}>
        <DialogTitle>Framework Configuration</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Title3>Notifications</Title3>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text weight="semibold">Notify on enforcement failure</Text>
                  <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                    Send alerts when controls fail
                  </Text>
                </div>
                <Switch
                  checked={frameworkConfig.notifyOnFailure}
                  onChange={(_, data) => setFrameworkConfig({ ...frameworkConfig, notifyOnFailure: data.checked })}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text weight="semibold">Notify on improvements</Text>
                  <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                    Send alerts when enforcement rate improves
                  </Text>
                </div>
                <Switch
                  checked={frameworkConfig.notifyOnImprovement}
                  onChange={(_, data) => setFrameworkConfig({ ...frameworkConfig, notifyOnImprovement: data.checked })}
                />
              </div>

              <Divider />

              <Title3>Thresholds</Title3>
              <Field label="Minimum enforcement rate (%)" hint="Alerts trigger below this threshold">
                <Input
                  type="number"
                  value={frameworkConfig.thresholdScore.toString()}
                  onChange={(_, data) => setFrameworkConfig({ ...frameworkConfig, thresholdScore: parseInt(data.value) || 80 })}
                  min={0}
                  max={100}
                />
              </Field>

              <Divider />

              <Title3>Automation</Title3>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text weight="semibold">Auto-remediate findings</Text>
                  <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                    Automatically apply fixes when available
                  </Text>
                </div>
                <Switch
                  checked={frameworkConfig.autoRemediate}
                  onChange={(_, data) => setFrameworkConfig({ ...frameworkConfig, autoRemediate: data.checked })}
                />
              </div>

              <Field label="Scan schedule">
                <Dropdown
                  value={frameworkConfig.scanSchedule}
                  onOptionSelect={(_, data) => setFrameworkConfig({ ...frameworkConfig, scanSchedule: data.optionValue as string })}
                >
                  <Option value="hourly">Hourly</Option>
                  <Option value="daily">Daily</Option>
                  <Option value="weekly">Weekly</Option>
                  <Option value="manual">Manual only</Option>
                </Dropdown>
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setIsConfigDialogOpen(false)}>Cancel</Button>
            <Button appearance="primary" onClick={() => setIsConfigDialogOpen(false)}>Save Configuration</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );

  let listBody: ReactNode;
  if (frameworksLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (frameworksError) {
    listBody = <ErrorState message="Unable to load enforcement frameworks." onRetry={refetch} />;
  } else if (frameworks.length === 0) {
    listBody = (
      <EmptyState
        icon={<DocumentRegular />}
        title="Frameworks Ready for Activation"
        description="Enable your first compliance framework to start evaluating controls and generating enforcement data."
        actionLabel="Enable Frameworks"
        onAction={() => navigate('/frameworks')}
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !frameworksLoading}>
        <Table aria-label="Enforcement frameworks">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Enabled</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'code' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('code')}
                onClick={() => toggleSort('code')}
              >
                Code
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('name')}
                onClick={() => toggleSort('name')}
                style={{ width: '100%' }}
              >
                Framework
              </TableHeaderCell>
              <TableHeaderCell>Version</TableHeaderCell>
              <TableHeaderCell>Controls</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'complianceScore' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('complianceScore')}
                onClick={() => toggleSort('complianceScore')}
              >
                Enforcement
              </TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>
                Actions
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {frameworks.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Switch
                    checked={!!item.isEnabled}
                    onChange={(_, data) =>
                      toggleFrameworkMutation.mutate({ id: item.id, enabled: data.checked })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Badge appearance="outline">{item.code}</Badge>
                </TableCell>
                <TableCell>
                  <Body1 style={{ fontWeight: 500 }}>{item.name}</Body1>
                </TableCell>
                <TableCell>
                  <Body2>{item.version || '-'}</Body2>
                </TableCell>
                <TableCell>
                  <Body2>{item.controlCount}</Body2>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <ProgressBar
                      value={(item.complianceScore ?? 0) / 100}
                      thickness="large"
                      style={{ width: '100px' }}
                    />
                    <Body2 style={{ color: getComplianceColor(item.complianceScore ?? 0) }}>
                      {item.complianceScore != null && !isNaN(item.complianceScore) ? `${item.complianceScore}%` : 'Not yet calculated'}
                    </Body2>
                  </div>
                </TableCell>
                <TableCell>
                  {/* §18 detail chevron — read-only list, no per-row mutation, so this is the only
                      row affordance. Not role-gated (read permission on this view is unconditional). */}
                  <Button
                    appearance="subtle"
                    icon={<ChevronRight24Regular />}
                    size="small"
                    aria-label="View framework details"
                    onClick={() => navigate(`/frameworks/${item.referenceId}`)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.fixed}>
        <PageHeader
          title="Governance Posture"
          subtitle="Board-level view of compliance status across all active frameworks"
        />
      </div>

      {/* AI Narrator — compliance insight */}
      <div className={styles.fixed}>
        <NarratorCard contextType="Framework" contextData={dashboard ? {
          overallScore: dashboard.overallScore,
          scoreChange24h: dashboard.scoreChange24h,
          totalFrameworks: dashboard.totalFrameworks,
          enabledFrameworks: dashboard.enabledFrameworks,
          criticalFindings: dashboard.criticalFindings,
          highFindings: dashboard.highFindings,
          frameworks: frameworks.slice(0, 5).map(f => ({ code: f.code, name: f.name, complianceScore: f.complianceScore })),
        } : undefined} />
      </div>

      {/* Overall Enforcement Rate */}
      <div className={styles.fixed}>
        <Card>
          <CardHeader
            image={
              <ShieldCheckmarkRegular
                style={{
                  fontSize: '2rem',
                  color: getComplianceColor(dashboard?.overallScore ?? 0),
                }}
              />
            }
            header={<Title2>Control Confidence Score</Title2>}
            description={
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Title1 style={{ color: getComplianceColor(dashboard?.overallScore ?? 0) }}>
                  {dashboard?.overallScore != null && !isNaN(dashboard.overallScore) ? `${dashboard.overallScore}%` : 'Not yet calculated'}
                </Title1>
                <ProgressBar
                  value={(dashboard?.overallScore ?? 0) / 100}
                  thickness="large"
                  style={{ width: '200px' }}
                />
              </div>
            }
          />
          <div className={ps.cardBody}>
            <div className={ps.statGrid}>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Total Frameworks</Body2>
                <Title2>{dashboard?.totalFrameworks ?? 0}</Title2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Enabled Frameworks</Body2>
                <Title2 style={{ color: tokens.colorPaletteGreenForeground1 }}>
                  {dashboard?.enabledFrameworks ?? 0}
                </Title2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Score Change (24h)</Body2>
                <Title2 style={{ color: (dashboard?.scoreChange24h ?? 0) >= 0 ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteRedForeground1 }}>
                  {(dashboard?.scoreChange24h ?? 0) >= 0 ? '+' : ''}{dashboard?.scoreChange24h ?? 0}%
                </Title2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Critical Findings</Body2>
                <Title2 style={{ color: tokens.colorPaletteRedForeground1 }}>
                  {dashboard?.criticalFindings ?? 0}
                </Title2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>High Findings</Body2>
                <Title2 style={{ color: tokens.colorPaletteRedForeground2 }}>
                  {dashboard?.highFindings ?? 0}
                </Title2>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Frameworks List — List & Grid Standard: ListPageLayout + Table + Pager, fills remaining height. */}
      <div className={styles.listSection}>
        <ListPageLayout
          header={
            <MasterDetailHeader
              crumbs={frameworksCrumbs}
              actions={configureDialog}
              busy={isFetching && !frameworksLoading}
            />
          }
          toolbar={
            <ListToolbar
              search={state.search}
              onSearchChange={setSearch}
              searchPlaceholder="Search frameworks…"
              onRefresh={refetch}
              isRefreshing={isFetching}
              testIdPrefix="frameworks"
            />
          }
          footer={
            <Pager
              page={state.page}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="frameworks-pager"
            />
          }
        >
          {listBody}
        </ListPageLayout>
      </div>
    </div>
  );
};
